import { useState } from 'react';

import { Button } from '@/components/ui/Button';
import { Empty } from '@/components/ui/EmptyState';
import { Grid, XStack, YStack } from '@/components/ui/layout/layout';
import { SegmentedControl } from '@/components/ui/segmented-control';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

import { DEFAULT_TEAM_USAGE_DAYS, useTeamUsageQuery } from '../../team.query';

import { FeatureAdoptionCard } from './FeatureAdoptionCard';
import { MembersByUsageTable } from './MembersByUsageTable';
import { MessagesOverTimeCard } from './MessagesOverTimeCard';
import { TeamUsageMemberDrawer } from './TeamUsageMemberDrawer';
import { TopIntegrationsCard } from './TopIntegrationsCard';
import { formatRangeLabel } from './usage-format';
import { UsageActivityTotals } from './UsageActivityTotals';
import { UsageMembersBar } from './UsageMembersBar';

const RANGE_OPTIONS = [7, 30, 90] as const;
type RangeDays = (typeof RANGE_OPTIONS)[number];

export function TeamUsageView({
  teamId,
  onInviteMember,
}: {
  teamId: string;
  onInviteMember: () => void;
}) {
  const [days, setDays] = useState<RangeDays>(DEFAULT_TEAM_USAGE_DAYS);
  const [selectedMemberId, setSelectedMemberId] = useState<string | null>(null);

  const { data, isLoading, isError, isPlaceholderData, refetch } =
    useTeamUsageQuery(teamId, days);

  if (isLoading) {
    return <TeamUsageSkeleton />;
  }
  if (isError || !data) {
    return (
      <Empty.Root variant="compact" className="py-16">
        <Empty.Header>
          <Empty.Title className="text-base">
            Failed to load usage analytics
          </Empty.Title>
          <Empty.Description>
            Check your connection and try again.
          </Empty.Description>
        </Empty.Header>
        <Empty.Actions>
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            Try again
          </Button>
        </Empty.Actions>
      </Empty.Root>
    );
  }

  const daysLabel = `Last ${data.window.days} days`;
  const rangeLabel = formatRangeLabel(data.window.start, data.window.days);
  const activeMembers = data.members.filter((m) => m.is_active).length;
  const selectedMember =
    data.members.find((m) => m.membership_id === selectedMemberId) ?? null;

  return (
    <YStack
      className={cn(
        'gap-4 transition-opacity duration-200',
        isPlaceholderData && 'opacity-60'
      )}
    >
      <XStack className="items-center justify-between gap-4">
        <YStack className="gap-0.5">
          <span className="text-base font-medium text-foreground">
            Adoption &amp; engagement
          </span>
          <span className="text-sm text-foreground-muted">
            {daysLabel} · {rangeLabel}
          </span>
        </YStack>
        <SegmentedControl.Root
          value={String(days)}
          onValueChange={(value) => setDays(Number(value) as RangeDays)}
        >
          <SegmentedControl.List className="h-9">
            {RANGE_OPTIONS.map((option) => (
              <SegmentedControl.Trigger key={option} value={String(option)}>
                {option} days
              </SegmentedControl.Trigger>
            ))}
          </SegmentedControl.List>
        </SegmentedControl.Root>
      </XStack>

      <UsageMembersBar
        activeMembers={activeMembers}
        totalMembers={data.members.length}
        onInviteMember={onInviteMember}
      />
      <UsageActivityTotals
        totals={data.totals}
        previousTotals={data.previous_totals}
        days={data.window.days}
      />
      <MessagesOverTimeCard
        subtitle={rangeLabel}
        series={data.messages_by_day}
      />

      <Grid className="grid-cols-1 gap-4 lg:grid-cols-[11fr_9fr]">
        <TopIntegrationsCard
          topIntegrations={data.top_integrations}
          totalActions={data.totals.integration_actions}
        />
        <FeatureAdoptionCard adoption={data.feature_adoption} />
      </Grid>

      <MembersByUsageTable
        members={data.members}
        onSelectMember={setSelectedMemberId}
        className="mt-4"
      />

      <TeamUsageMemberDrawer
        member={selectedMember}
        members={data.members}
        window={data.window}
        onClose={() => setSelectedMemberId(null)}
      />
    </YStack>
  );
}

function TeamUsageSkeleton() {
  return (
    <YStack className="gap-4">
      <XStack className="items-center justify-between gap-4">
        <YStack className="gap-0.5">
          <Skeleton className="h-5 w-48" />
          <Skeleton className="h-4 w-64" />
        </YStack>
        <Skeleton className="h-9 w-60 rounded-lg" />
      </XStack>
      <Skeleton className="h-16 rounded-xl" />
      <Grid className="grid-cols-2 gap-3 md:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-[4.375rem] rounded-lg" />
        ))}
      </Grid>
      <Skeleton className="h-[17.5rem] rounded-xl" />
      <Grid className="grid-cols-1 gap-4 lg:grid-cols-[11fr_9fr]">
        <Skeleton className="h-[15rem] rounded-xl" />
        <Skeleton className="h-[15rem] rounded-xl" />
      </Grid>
      <Skeleton className="mt-4 h-[24rem] rounded-xl" />
    </YStack>
  );
}
